import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import './Login.css';
import CityAutocomplete from '../common/CityAutocomplete';
import { auth } from '../../firebase';
import { reload } from 'firebase/auth';
import backgroundImage from '../../assets/common/2nd_background.png';

// Determine API base URL (keep logic consistent with AuthContext and other pages)
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  // If no env URL, use localhost for development
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  // If it's a placeholder URL, use localhost for development
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  // If env URL contains 'run.app', it's a Cloud Run URL - always use it
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  // In production builds, always use the configured URL
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }

  // In development, use localhost
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function Signup() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [showEmailSignup, setShowEmailSignup] = useState(false);
  const [activeSignupTab, setActiveSignupTab] = useState('google'); // 'google', 'email'
  const [isSignupMode, setIsSignupMode] = useState(true); // Always in signup mode
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [needsVerificationCode, setNeedsVerificationCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  // Phone verification state
  const [needsPhoneVerification, setNeedsPhoneVerification] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false); // Track if phone has been verified
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [businessType, setBusinessType] = useState('');
  const [businessDisplayName, setBusinessDisplayName] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [freelancerDisplayName, setFreelancerDisplayName] = useState('');
  const [freelancerPhone, setFreelancerPhone] = useState('');
  const [freelancerLocation, setFreelancerLocation] = useState('');
  const [freelancerLocationValid, setFreelancerLocationValid] = useState(false);
  const [freelancerCategory, setFreelancerCategory] = useState('');
  const [jobCategories, setJobCategories] = useState([]);
  const [businessLocation, setBusinessLocation] = useState('');
  const [businessLocationValid, setBusinessLocationValid] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [checkingDisplayName, setCheckingDisplayName] = useState(false);
  const { login, signup, loginWithGoogle, currentUser, userRoles, addUserRole, logout, loading: authLoading, switchingRole, resendVerificationCode, isApproved, addPassword, loadUserRoles } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [showAddPasswordForm, setShowAddPasswordForm] = useState(false);
  const [addingPassword, setAddingPassword] = useState(false);
  const [needsGoogleSignIn, setNeedsGoogleSignIn] = useState(false);
  const [pendingEmailForPassword, setPendingEmailForPassword] = useState('');

  // Check if business user is missing business type or display name
  const [needsBusinessType, setNeedsBusinessType] = useState(false);
  const [needsBusinessDisplayName, setNeedsBusinessDisplayName] = useState(false);
  const [checkingBusinessRequirements, setCheckingBusinessRequirements] = useState(false);
  const [businessRequirementsChecked, setBusinessRequirementsChecked] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Sync activeSignupTab with showEmailSignup
  useEffect(() => {
    if (showEmailSignup && activeSignupTab !== 'email') {
      setActiveSignupTab('email');
    } else if (!showEmailSignup && activeSignupTab === 'email') {
      setActiveSignupTab('google');
    }
  }, [showEmailSignup]);

  // Clear verification state when switching away from email signup
  useEffect(() => {
    if (activeSignupTab !== 'email') {
      setPassword('');
      setConfirmPassword('');
      setVerificationCode('');
      setNeedsVerificationCode(false);
      setNeedsGoogleSignIn(false);
      setPendingEmailForPassword('');
    }
    // Don't clear phone verification state when switching tabs - it's required after signup
  }, [activeSignupTab]);

  // Check if user signed in with Google after being prompted to add password
  useEffect(() => {
    if (needsGoogleSignIn && currentUser && currentUser.email) {
      // Check if the email matches the one we were trying to sign in with
      if (pendingEmailForPassword && currentUser.email.toLowerCase() === pendingEmailForPassword.toLowerCase()) {
        // Check if user signed in with Google
        const providerIds = (currentUser.providerData || []).map((p) => p.providerId);
        if (providerIds.includes('google.com')) {
          // User signed in with Google, now show add password form
          setNeedsGoogleSignIn(false);
          setShowAddPasswordForm(true);
          setShowEmailSignup(true); // Ensure email signup form is visible
          setLoading(false);
          setIsAuthenticating(false);
          setError(t('login.nowAddPassword') || 'Great! Now you can add a password to your account.');
        }
      }
    }
  }, [needsGoogleSignIn, currentUser, pendingEmailForPassword, t]);

  // Check if business user needs to provide business type or display name
  useEffect(() => {
    const checkBusinessRequirements = async () => {
      // Only check if we have a current user and auth is not loading
      if (!currentUser || authLoading) {
        // If no user or still loading, clear requirements and mark as not checking
        setNeedsBusinessType(false);
        setNeedsBusinessDisplayName(false);
        setCheckingBusinessRequirements(false);
        return;
      }

      // Check if user is a business user
      if (userRoles && userRoles.length > 0 && userRoles.includes('business')) {
        setCheckingBusinessRequirements(true);
        setBusinessRequirementsChecked(false);
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../../firebase');
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const missingBusinessType = !userData.businessType || !userData.businessType.trim();
            const missingDisplayName = !userData.displayName || !userData.displayName.trim();

            if (missingBusinessType || missingDisplayName) {
              setNeedsBusinessType(missingBusinessType);
              setNeedsBusinessDisplayName(missingDisplayName);
              setSelectedRole('business');
              if (userData.businessType) setBusinessType(userData.businessType);
              if (userData.displayName) setBusinessDisplayName(userData.displayName);
            } else {
              setNeedsBusinessType(false);
              setNeedsBusinessDisplayName(false);
            }
          } else {
            // User document doesn't exist, assume both are needed
            setNeedsBusinessType(true);
            setNeedsBusinessDisplayName(true);
            setSelectedRole('business');
          }
        } catch (error) {
          console.error('Error checking business requirements:', error);
          // Error checking business requirements
          // On error, assume both are needed to be safe
          setNeedsBusinessType(true);
          setNeedsBusinessDisplayName(true);
          setSelectedRole('business');
        } finally {
          setCheckingBusinessRequirements(false);
          setBusinessRequirementsChecked(true);
        }
      } else if (userRoles && userRoles.length > 0) {
        // User has roles but not business, clear business requirements
        setNeedsBusinessType(false);
        setNeedsBusinessDisplayName(false);
        setCheckingBusinessRequirements(false);
        setBusinessRequirementsChecked(true);
      } else {
        // No roles yet or userRoles not loaded, clear business requirements
        // But don't set checkingBusinessRequirements to false if we're still waiting for userRoles
        if (userRoles !== undefined) {
          // userRoles has been loaded (even if empty), so we're done checking
          setNeedsBusinessType(false);
          setNeedsBusinessDisplayName(false);
          setCheckingBusinessRequirements(false);
          setBusinessRequirementsChecked(true);
        }
        // If userRoles is undefined, we're still loading, so leave checkingBusinessRequirements as is
      }
    };
    checkBusinessRequirements();
  }, [currentUser, userRoles, authLoading]);

  // Handle redirect after login and roles/approval have been checked
  useEffect(() => {
    // Wait for auth and business requirements check to finish loading
    if (authLoading || checkingBusinessRequirements) {
      return;
    }

    // Don't redirect if we're showing the add-password form - user needs to add password first
    if (showAddPasswordForm || needsGoogleSignIn) {
      return;
    }

    // Don't redirect if phone verification is needed
    if (needsPhoneVerification) {
      return;
    }

      // Only redirect if user is logged in, approved, and all checks are complete
      const isAdmin = userRoles && userRoles.includes('admin');
      const isApprovedUser = isAdmin || !!isApproved;

      if (currentUser && isApprovedUser && !authLoading && !checkingBusinessRequirements) {
        // Check if this is a new Google signup (no roles yet) - require phone verification
        const isGoogleUser = currentUser.providerData && currentUser.providerData.some(
          (provider) => provider.providerId === 'google.com'
        );
        const hasNoRoles = !userRoles || userRoles.length === 0;

        // If user is a Google user with no roles and phone hasn't been verified yet, require phone verification first
        if (isGoogleUser && hasNoRoles && !needsPhoneVerification && !phoneVerified) {
          setNeedsPhoneVerification(true);
          setLoading(false);
          setIsAuthenticating(false);
          return;
        }

        // userRoles is always an array (initialized as [] in AuthContext)
        // If it has roles, check business requirements and redirect
        if (userRoles && userRoles.length > 0) {
          // Check if business user is missing business type or display name
          // IMPORTANT: For business users, we MUST wait for requirements check to complete
          if (userRoles.includes('business')) {
            // For business users, we must explicitly wait for the requirements check to complete
            if (!businessRequirementsChecked) {
              // Requirements check hasn't completed yet, wait
              return;
            }
            // If requirements are missing, redirect to role selection page
            if (needsBusinessType || needsBusinessDisplayName) {
              setLoading(false);
              setIsAuthenticating(false);
              navigate('/role-selection', { replace: true });
              return;
            }
          }
          // User has at least one role and business type/display name are set (if business), go to appropriate landing page
          setLoading(false);
          setIsAuthenticating(false);
          const defaultPath = userRoles.includes('freelancer')
            ? '/freelancer/dashboard'
            : userRoles.includes('business')
              ? '/jobs'
              : '/profile';
          navigate(defaultPath, { replace: true });
          return;
        }
        // If no roles (empty array) and we're not loading, redirect to role selection
        setLoading(false);
        setIsAuthenticating(false);
        navigate('/role-selection', { replace: true });
      } else if (currentUser && !isApprovedUser && !authLoading) {
        // User is logged in but not approved - stay on login page, don't redirect
        // This prevents redirect loops when user comes from unauthorized page
        setLoading(false);
        setIsAuthenticating(false);
      } else if (!currentUser && !authLoading && !isAuthenticating) {
        // User is not logged in and auth is done loading
        // Only reset loading if we're not actively authenticating
        setLoading(false);
      }
    }, [currentUser, userRoles, authLoading, checkingBusinessRequirements, loading, navigate, needsBusinessType, needsBusinessDisplayName, isApproved, businessRequirementsChecked, isAuthenticating, needsPhoneVerification, phoneVerified]);

  // Clear form fields when switching roles
  useEffect(() => {
    if (switchingRole || (userRoles && userRoles.length === 0)) {
      setBusinessType('');
      setBusinessDisplayName('');
      setBusinessPhone('');
      setFreelancerDisplayName('');
      setFreelancerCategory('');
      setBusinessLocation('');
      setBusinessLocationValid(false);
      setSelectedRole('');
    }
  }, [switchingRole, userRoles]);

  // Load freelancer job categories when freelancer role is selected
  useEffect(() => {
    const fetchJobCategories = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/job-categories`);
        if (!res.ok) return;
        const data = await res.json();
        const categories = Array.isArray(data.categories) ? data.categories : [];
        setJobCategories(categories);
      } catch (e) {
        console.warn('Failed to load job categories', e);
      }
    };

    if (selectedRole === 'freelancer' && jobCategories.length === 0) {
      fetchJobCategories();
    }
  }, [selectedRole, jobCategories.length]);

  // Israeli phone validation
  const isValidIsraeliPhone = (input) => {
    if (!input) return false;
    const normalized = input.replace(/[\s\-]/g, '');
    let local = normalized;
    if (local.startsWith('+972')) {
      local = '0' + local.slice(4);
    }
    const landline = /^0[23489]\d{7}$/;     // e.g., 03-XXXXXXX
    const mobileOrVoip = /^0[57]\d{8}$/;    // e.g., 05X-XXXXXXX, 07X-XXXXXXX
    return landline.test(local) || mobileOrVoip.test(local);
  };

  async function handleSendPhoneCode() {
    if (!phoneNumber.trim()) {
      setError(t('login.phoneRequired') || 'Phone number is required');
      return;
    }

    if (!isValidIsraeliPhone(phoneNumber)) {
      setError(t('login.invalidPhone') || 'Please enter a valid Israeli phone number');
      return;
    }

    setSendingPhoneCode(true);
    setError('');

    try {
      // TODO: Implement actual phone code sending API call
      // For now, just show success message
      setPhoneCodeSent(true);
      setError(t('login.phoneCodeSent') || 'Verification code will be sent to your phone. (Implementation pending)');
      // In the future, this will call an API endpoint to send the code
      // const token = await currentUser.getIdToken();
      // await fetch(`${API_BASE_URL}/api/auth/send-phone-code`, {
      //   method: 'POST',
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //     'Content-Type': 'application/json'
      //   },
      //   body: JSON.stringify({ phoneNumber: phoneNumber.trim() })
      // });
    } catch (err) {
      console.error('Error sending phone code:', err);
      setError(err.message || t('login.failedToSendPhoneCode') || 'Failed to send verification code');
    } finally {
      setSendingPhoneCode(false);
    }
  }

  async function handleVerifyPhoneCode() {
    if (!phoneVerificationCode.trim() || phoneVerificationCode.trim().length !== 6) {
      setError(t('login.invalidVerificationCode') || 'Please enter a valid 6-digit verification code');
      return;
    }

    setLoading(true);
    setIsAuthenticating(true);
    setError('');

    try {
      // TODO: Implement actual phone verification API call
      // For now, just mark as verified
      // In the future, this will call an API endpoint to verify the code
      // const token = await currentUser.getIdToken();
      // const response = await fetch(`${API_BASE_URL}/api/auth/verify-phone-code`, {
      //   method: 'POST',
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //     'Content-Type': 'application/json'
      //   },
      //   body: JSON.stringify({
      //     phoneNumber: phoneNumber.trim(),
      //     code: phoneVerificationCode.trim()
      //   })
      // });

      // Simulate verification success for now
      setNeedsPhoneVerification(false);
      setPhoneVerified(true); // Mark phone as verified
      setPhoneNumber('');
      setPhoneVerificationCode('');
      setPhoneCodeSent(false);
      setError('');

      // Reload user to get updated status
      if (currentUser) {
        await reload(currentUser);
        if (loadUserRoles) {
          await loadUserRoles(currentUser.uid);
        }
      }

      setLoading(false);
      setIsAuthenticating(false);

      // After phone verification, wait for isApproved to be set (Google users are auto-approved)
      // Then navigate to role selection
      const checkAndNavigate = () => {
        // Check if user is Google user (auto-approved) or if isApproved is true
        const isGoogleUser = currentUser?.providerData?.some(
          (provider) => provider.providerId === 'google.com'
        );

        if (isGoogleUser || isApproved) {
          navigate('/role-selection', { replace: true });
        } else {
          // If not approved yet, wait a bit more and check again
          setTimeout(checkAndNavigate, 100);
        }
      };

      // Start checking after a short delay
      setTimeout(checkAndNavigate, 300);
    } catch (err) {
      console.error('Phone verification error:', err);
      setError(err.message || t('login.invalidVerificationCode') || 'Invalid verification code. Please try again.');
      setLoading(false);
      setIsAuthenticating(false);
    }
  }
  const handleRoleConfirm = async () => {
    // If needs business type or display name (existing business user), just update them
    if ((needsBusinessType || needsBusinessDisplayName) && currentUser) {
      if (!businessType.trim()) {
        alert(t('login.businessTypeRequired'));
        return;
      }
      if (!businessDisplayName.trim()) {
        alert(t('login.businessDisplayNameRequired'));
        return;
      }
      if (!businessLocation.trim() || !businessLocationValid) {
        alert('Please select a city from the list');
        return;
      }

      setSavingRole(true);
      setCheckingDisplayName(true);
      try {
        // Check for duplicate display name via API
        const token = await currentUser.getIdToken();

        // Check if display name is already taken
        const checkResponse = await fetch(`${API_BASE_URL}/api/user/check-display-name?displayName=${encodeURIComponent(businessDisplayName.trim())}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        setCheckingDisplayName(false);

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          if (!checkData.available) {
            alert(t('login.displayNameTaken'));
            setSavingRole(false);
            return;
          }
        }

        // Use backend API to update profile (ensures validation)
        // Always write businessType and displayName for business users,
        // so that the Firestore "displayName" field matches the business display name.
        const updateData = {
          businessType: businessType.trim(),
          displayName: businessDisplayName.trim(),
          location: businessLocation.trim()
        };

        let updateResponse;
        try {
          const apiUrl = `${API_BASE_URL}/api/user/profile`;
          console.log('Attempting to update profile at:', apiUrl);
          updateResponse = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          });
        } catch (networkError) {
          // Handle network errors (API not reachable, CORS, etc.)
          //console.error.'Network error saving business requirements:', networkError);
          //console.error.'API Base URL being used:', API_BASE_URL);
          //console.error.'Full error:', networkError);
          throw new Error(`Network error: Unable to connect to server at ${API_BASE_URL}. Please check your connection and ensure the backend is running.`);
        }

        if (!updateResponse.ok) {
          let errorMessage = 'Failed to update profile';
          try {
            const errorData = await updateResponse.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
            //console.error.'Profile update error:', errorData);
          } catch (parseError) {
            // If response is not JSON, use status text
            errorMessage = updateResponse.statusText || errorMessage;
            //console.error.'Profile update failed with status:', updateResponse.status, updateResponse.statusText);
          }
          throw new Error(errorMessage);
        }
        setNeedsBusinessType(false);
        setNeedsBusinessDisplayName(false);
        setSavingRole(false);
        // After updating profile, wait a moment for Firestore to propagate the update
        // then reload the page to ensure App.js re-checks business requirements with fresh data
        // Using a small delay to allow Firestore write to complete and propagate
        setTimeout(() => {
          window.location.href = '/jobs';
        }, 1000);
      } catch (error) {
        // Error saving business requirements
        // Show specific error messages
        if (error.message && error.message.includes('Display name already taken')) {
          alert(t('login.displayNameTaken'));
        } else if (error.message && error.message.includes('Display name is required')) {
          alert(t('login.businessDisplayNameRequired'));
        } else if (error.message && error.message.includes('Unauthorized')) {
          alert(t('login.failedToSaveRole') + ' (Unauthorized)');
        } else if (error.message && error.message.includes('Network error')) {
          alert(error.message);
        } else if (error.message && error.message.includes('Failed to update profile')) {
          alert(error.message);
        } else {
          alert(t('login.failedToSaveRole') + (error.message ? ': ' + error.message : ''));
        }
        setSavingRole(false);
        setCheckingDisplayName(false);
      }
      return;
    }

    if (selectedRole && currentUser) {
      // Get display name based on role
      const displayName = selectedRole === 'business' ? businessDisplayName.trim() : freelancerDisplayName.trim();

      // Validate based on role
      if (selectedRole === 'business') {
        if (!businessType.trim()) {
          alert(t('login.businessTypeRequired'));
          return;
        }
        if (!displayName) {
          alert(t('login.businessDisplayNameRequired'));
          return;
        }
        if (!businessLocation.trim() || !businessLocationValid) {
          alert('Please select a city from the list');
          return;
        }
      } else if (selectedRole === 'freelancer') {
        if (!displayName) {
          alert(t('login.displayNameRequired'));
          return;
        }
        if (!freelancerLocation.trim() || !freelancerLocationValid) {
          alert('Please select a city from the list');
          return;
        }
        if (jobCategories.length > 0 && !freelancerCategory) {
          alert(t('login.freelancerCategoryRequired') || 'Please select a category');
          return;
        }
      }

      setSavingRole(true);
      setCheckingDisplayName(true);
      try {
        // Check for duplicate display name before adding role (for both business and freelancer)
        const token = await currentUser.getIdToken();

        const checkResponse = await fetch(`${API_BASE_URL}/api/user/check-display-name?displayName=${encodeURIComponent(displayName)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        setCheckingDisplayName(false);

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          if (!checkData.available) {
            alert(t('login.displayNameTaken'));
            setSavingRole(false);
            return;
          }
        }

        console.log('Attempting to add role:', selectedRole);
        await addUserRole(
          currentUser.uid,
          selectedRole,
          selectedRole === 'business' ? businessType.trim() : null,
          displayName
        );
        console.log('Role added, navigating to dashboard');
        // Save contact info if applicable
        try {
          if (selectedRole === 'freelancer') {
            const token2 = await currentUser.getIdToken();
            await fetch(`${API_BASE_URL}/api/user/profile`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token2}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                displayName,
                location: freelancerLocation.trim(),
                freelancerCategory: freelancerCategory || null
              })
            });
          } else if (selectedRole === 'business') {
            const token2 = await currentUser.getIdToken();
            await fetch(`${API_BASE_URL}/api/user/profile`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token2}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                displayName,
                businessType: businessType.trim(),
                location: businessLocation.trim()
              })
            });
          }
        } catch (err) {
          console.warn('Failed to save freelancer contact info', err);
        }
        const targetPath = selectedRole === 'freelancer' ? '/freelancer/dashboard' : '/jobs';
        navigate(targetPath);
      } catch (error) {
        //console.error.'Error adding role:', error);
        if (error.message && error.message.includes('Display name already taken')) {
          alert(t('login.displayNameTaken'));
        } else {
          alert(t('login.failedToSaveRole'));
        }
        setSavingRole(false);
        setCheckingDisplayName(false);
      }
    }
  };

  async function handleVerifyCode() {
    // Set loading immediately for instant feedback
    setLoading(true);
    setIsAuthenticating(true);
    setError('');

    if (!currentUser) {
      setError(t('login.pleaseSignInToVerify') || 'Please sign in first to verify your email');
      setLoading(false);
      setIsAuthenticating(false);
      return;
    }

    if (!verificationCode.trim() || verificationCode.trim().length !== 6) {
      setError(t('login.invalidVerificationCode') || 'Please enter a valid 6-digit verification code');
      setLoading(false);
      setIsAuthenticating(false);
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-email-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: verificationCode.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify code');
      }

      // Verification successful - reload user to get updated approval status
      await reload(currentUser);

      // Explicitly reload user roles to get updated approval status
      // This ensures isApproved is updated in AuthContext
      if (currentUser && loadUserRoles) {
        await loadUserRoles(currentUser.uid);
      }

      setNeedsVerificationCode(false);
      setVerificationCode('');
      setError('');

      // After email verification, require phone verification
      setNeedsPhoneVerification(true);
      setLoading(false);
      setIsAuthenticating(false);
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || t('login.invalidVerificationCode') || 'Invalid verification code. Please try again.');
      setIsAuthenticating(false);
      setLoading(false);
    }
  }

  async function handleEmailPasswordSignup() {
    // Set loading immediately for instant feedback
    setLoading(true);
    setIsAuthenticating(true);
    setError('');

    try {
      if (!email.trim()) {
        setError(t('login.emailRequired') || 'Email is required');
        setLoading(false);
        setIsAuthenticating(false);
        return;
      }
      if (!password.trim()) {
        setError(t('login.passwordRequired') || 'Password is required');
        setLoading(false);
        setIsAuthenticating(false);
        return;
      }
      if (password.length < 6) {
        setError(t('login.passwordTooShort') || 'Password must be at least 6 characters');
        setLoading(false);
        setIsAuthenticating(false);
        return;
      }
      if (password !== confirmPassword) {
        setError(t('login.passwordsDoNotMatch') || 'Passwords do not match');
        setLoading(false);
        setIsAuthenticating(false);
        return;
      }

      try {
        const result = await signup(email.trim(), password.trim());

        // After signup, ALWAYS require email verification for email/password accounts.
        // Show the 6-digit code input immediately.
        // Keep loading true briefly to show that the verification code is being sent
        if (result && result.user) {
          setNeedsVerificationCode(true);
          setError(t('login.verifyEmailCodeInstructions') || 'Please check your email and enter the 6-digit verification code.');
          // Add a small delay to ensure user sees the loading state while code is being sent
          // The backend sends the code synchronously, but we want to show loading state
          setTimeout(() => {
            setLoading(false);
            setIsAuthenticating(false);
          }, 500);
        }
      } catch (err) {
        console.error('Email/password signup error:', err);
        setLoading(false);
        setIsAuthenticating(false);
        // Check if this is a Google account that needs password added
        if (err.code === 'auth/google-account-exists' || (err.message && err.message.includes('already registered with Google'))) {
          // Redirect to login page if account exists
          setError(t('login.emailAlreadyInUse') || 'This email is already registered. Please sign in instead.');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else if (err.code === 'auth/email-already-in-use' || (err.message && err.message.includes('already in use'))) {
          setError(t('login.emailAlreadyInUse') || 'This email is already in use. Please sign in instead.');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          const msg = err.message || err.toString();
          setError((t('login.failedToSignup') || 'Failed to sign up') + ': ' + msg);
        }
      }
    } catch (outerErr) {
      console.error('Unexpected error in email/password signup:', outerErr);
      setError(t('login.failedToSignup') || 'Failed to sign up');
      setLoading(false);
      setIsAuthenticating(false);
    }
  }

  // Signup component doesn't handle login - redirect to login page if needed
  // Verification code handling is done via handleVerifyCode

  async function handleAddPassword() {
    setAddingPassword(true);
    setError('');

    try {
      if (!password.trim()) {
        setError(t('login.passwordRequired') || 'Password is required');
        setAddingPassword(false);
        return;
      }

      if (password.length < 6) {
        setError(t('login.passwordTooShort') || 'Password must be at least 6 characters');
        setAddingPassword(false);
        return;
      }

      if (password !== confirmPassword) {
        setError(t('login.passwordsDoNotMatch') || 'Passwords do not match');
        setAddingPassword(false);
        return;
      }

      // Check if user is already authenticated
      if (!currentUser) {
        // User needs to sign in with Google first
        setError(t('login.signInWithGoogleFirst') || 'Please sign in with Google first, then you can add a password. Click "Continue with Google" above.');
        setAddingPassword(false);
        return;
      }

      // Check if user signed in with Google
      const providerIds = (currentUser.providerData || []).map((p) => p.providerId);
      if (!providerIds.includes('google.com')) {
        setError(t('login.addPasswordOnlyGoogle') || 'This feature is only available for Google-authenticated users. Please sign in with Google first.');
        setAddingPassword(false);
        return;
      }

      // Add password
      const result = await addPassword(password.trim());

      setError(t('login.passwordAddedSuccess') || 'Password added successfully! A verification code has been sent to your email. You will need to verify your email when signing in with email and password.');
      setShowAddPasswordForm(false);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error adding password:', err);
      setError(err.message || t('login.failedToAddPassword') || 'Failed to add password');
    } finally {
      setAddingPassword(false);
    }
  }

  async function handleGoogleLogin() {
    try {
      setError('');
      setLoading(true);
      setIsAuthenticating(true);
      // Ensure loading state is set immediately
      await new Promise(resolve => setTimeout(resolve, 0));
      const result = await loginWithGoogle();
      if (result && result.user) {
        // After Google signup, require phone verification
        setLoading(false);
        setIsAuthenticating(false);
        setNeedsPhoneVerification(true);
      } else {
        setLoading(false);
        setIsAuthenticating(false);
      }
    } catch (error) {
      // Show more detailed error message
      let errorMessage = error.message || error.toString();
      let userFriendlyMessage = errorMessage;
      // Handle specific error codes
      if (error.code === 'auth/invalid-credential') {
        userFriendlyMessage = t('login.invalidCredentialError') + ' ' + t('login.invalidCredentialHelp');
      } else if (error.code === 'auth/popup-blocked') {
        userFriendlyMessage = t('login.popupBlocked');
      } else if (error.code === 'auth/popup-closed-by-user') {
        userFriendlyMessage = t('login.popupClosed');
      } else if (error.code === 'auth/unauthorized-domain') {
        userFriendlyMessage = t('login.unauthorizedDomain');
      } else if (error.code === 'auth/operation-not-allowed') {
        userFriendlyMessage = t('login.googleNotEnabled');
      } else if (error.code === 'auth/internal-error' || errorMessage.includes('internal-error')) {
        userFriendlyMessage = t('login.internalError');
      }

      setError(t('login.failedToLogin') + ': ' + userFriendlyMessage);
      setLoading(false);
      setIsAuthenticating(false);
    }
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      //console.error.'Error logging out:', error);
      setError(t('login.failedToLogout') || 'Failed to logout');
    }
  };

  // Show loading state while checking auth and roles or business requirements
  if (authLoading || loading || checkingBusinessRequirements || switchingRole) {
    return (
      <div className={`loading-screen ${isRTL ? 'rtl' : ''}`}>
        <div className="loading-spinner-container">
          <span className="spinner" style={{ width: '32px', height: '32px', borderWidth: '3px', borderColor: '#2563EB', borderTopColor: '#60A5FA' }}></span>
          <p className="loading-text">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Show signup if user is not logged in
  return (
    <div className={`login-container ${isRTL ? 'rtl' : ''}`} style={{ backgroundImage: `url(${backgroundImage})` }}>
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-header-app-name">{t('app.name')}</h1>
          <h2 className="login-page-title">{t('login.signupPageTitle')}</h2>
          <p className="login-header-tagline">{t('app.tagline')}</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Progress Indicator */}
        {(needsVerificationCode || needsPhoneVerification) && (
          <div style={{
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: needsVerificationCode ? '#FFC720' : '#10B981',
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700',
              fontSize: '0.875rem',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              border: '2px solid',
              borderColor: needsVerificationCode ? '#FFC720' : '#10B981'
            }}>
              1
            </div>
            <div style={{
              width: '40px',
              height: '3px',
              background: needsPhoneVerification ? '#10B981' : (needsVerificationCode ? '#E5E7EB' : '#E5E7EB'),
              borderRadius: '2px'
            }}></div>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: needsPhoneVerification ? '#FFC720' : '#E5E7EB',
              color: needsPhoneVerification ? '#111827' : '#9CA3AF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700',
              fontSize: '0.875rem',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              border: '2px solid',
              borderColor: needsPhoneVerification ? '#FFC720' : '#E5E7EB'
            }}>
              2
            </div>
            {needsVerificationCode && (
              <div style={{
                width: '100%',
                textAlign: 'center',
                marginTop: '0.5rem',
                fontSize: '0.875rem',
                color: '#6B7280',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                fontWeight: '500'
              }}>
                {t('login.verifyEmailTitle') || 'Email Verification'}
              </div>
            )}
            {needsPhoneVerification && (
              <div style={{
                width: '100%',
                textAlign: 'center',
                marginTop: '0.5rem',
                fontSize: '0.875rem',
                color: '#6B7280',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                fontWeight: '500'
              }}>
                {t('login.phoneVerificationRequired') || 'Phone Verification'}
              </div>
            )}
          </div>
        )}

        {needsGoogleSignIn && (
          <div style={{
            marginBottom: '1rem',
            padding: '1rem',
            borderRadius: '8px',
            background: '#EFF6FF',
            border: '1px solid #BFDBFE',
            textAlign: 'center'
          }}>
            <p style={{ color: '#111827', marginBottom: '1rem', fontSize: '0.9rem' }}>
              {t('login.googleAccountNoPassword') || 'This email is registered with Google. Please sign in with Google first, then you can add a password.'}
            </p>
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="google-button"
              style={{
                opacity: loading ? 0.5 : 1,
                pointerEvents: loading ? 'none' : 'auto',
                width: '100%',
                marginTop: '0.5rem'
              }}
            >
              {loading ? (
                <span className="spinner spinner-small" style={{ marginRight: 0, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'rgba(255,255,255,0.9)' }}></span>
              ) : (
                <svg className="google-icon" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span className="google-button-text">
                {loading ? t('login.signingIn') : t('login.signInWithGoogle')}
              </span>
            </button>
          </div>
        )}

        {/* Only show tabs if not in verification mode */}
        {!needsVerificationCode && !needsPhoneVerification && (
          <div className="login-tabs-container" style={{ marginBottom: '1rem' }}>
            <div className="login-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeSignupTab === 'google'}
                className={`login-tab ${activeSignupTab === 'google' ? 'active' : ''}`}
                onClick={() => {
                  if (!loading) {
                    setActiveSignupTab('google');
                    setShowEmailSignup(false);
                  }
                }}
                disabled={loading}
              >
                <div className="login-tab-icon">
                  <svg className="google-icon-colored" viewBox="0 0 24 24" width="24" height="24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <span className="login-tab-label">{t('login.google') || 'Google'}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeSignupTab === 'email'}
                className={`login-tab ${activeSignupTab === 'email' ? 'active' : ''}`}
                onClick={() => {
                  if (!loading) {
                    setActiveSignupTab('email');
                    setShowEmailSignup(true);
                  }
                }}
                disabled={loading}
              >
                <div className="login-tab-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <span className="login-tab-label">{t('login.email') || 'Email'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Email Verification Screen - Focused View */}
        {needsVerificationCode && !needsPhoneVerification ? (
          <div className="email-login-form" style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(4px)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
                flexDirection: 'column',
                gap: '1.5rem',
                border: '2px solid rgba(37, 99, 235, 0.3)',
                boxShadow: '0 0 20px rgba(37, 99, 235, 0.2)'
              }}>
                <span className="spinner" style={{ width: '32px', height: '32px', borderWidth: '4px', borderColor: 'rgba(37, 99, 235, 0.2)', borderTopColor: '#2563EB' }}></span>
                <span style={{ color: '#111827', fontSize: '1rem', fontWeight: '500' }}>
                  {t('login.verifying') || 'Verifying...'}
                </span>
              </div>
            )}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1.25rem',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
              border: '1px solid #FECACA',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.1)'
            }}>
              <p style={{ margin: 0, color: '#DC2626', fontSize: '0.9375rem', lineHeight: '1.5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', fontWeight: '500' }}>
                {t('login.verifyEmailCodeInstructions') || 'Please check your email and enter the 6-digit verification code below.'}
              </p>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="verification-code" style={{ display: 'block', marginBottom: '0.75rem', color: '#111827', fontWeight: '600', fontSize: '1rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                {t('login.verificationCode') || 'Verification Code'}
              </label>
              <input
                id="verification-code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                placeholder="123456"
                className="signup-input verification-code-input"
                style={{
                  width: '100%',
                  padding: '1rem',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  background: 'white',
                  color: '#111827',
                  fontSize: '1.5rem',
                  letterSpacing: '0.5em',
                  textAlign: 'center',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  fontWeight: '700',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
                disabled={loading}
                autoComplete='off'
                autoFocus
                onFocus={(e) => {
                  e.target.style.borderColor = '#FFC720';
                  e.target.style.boxShadow = '0 0 0 3px rgba(255, 199, 32, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.boxShadow = 'none';
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && verificationCode.trim().length === 6) {
                    handleVerifyCode();
                  }
                }}
              />
            </div>
            <button
              type="button"
              onClick={handleVerifyCode}
              disabled={loading || verificationCode.trim().length !== 6}
              className="signup-primary-button"
              style={{
                width: '100%',
                padding: '0.875rem 1.5rem',
                borderRadius: '12px',
                border: 'none',
                background: (loading || verificationCode.trim().length !== 6) ? '#E5E7EB' : '#FFC720',
                color: (loading || verificationCode.trim().length !== 6) ? '#9CA3AF' : '#111827',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: (loading || verificationCode.trim().length !== 6) ? 'not-allowed' : 'pointer',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                transition: 'all 0.2s ease',
                boxShadow: (loading || verificationCode.trim().length !== 6) ? 'none' : '0 2px 8px rgba(255, 199, 32, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!loading && verificationCode.trim().length === 6) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 199, 32, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && verificationCode.trim().length === 6) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(255, 199, 32, 0.3)';
                }
              }}
            >
              {loading && (
                <span className="spinner spinner-small" style={{ marginRight: 0, borderColor: 'rgba(17, 24, 39, 0.3)', borderTopColor: 'rgba(17, 24, 39, 0.9)' }}></span>
              )}
              {loading ? (t('login.verifying') || 'Verifying...') : (t('login.verifyEmail') || 'Verify Email')}
            </button>
            <button
              type="button"
              onClick={async () => {
                setResendingCode(true);
                setError('');
                try {
                  await resendVerificationCode(email.trim());
                  setError(t('login.verificationCodeResent') || 'Verification code has been resent!');
                } catch (err) {
                  setError(err.message || t('login.failedToResendCode') || 'Failed to resend code');
                } finally {
                  setResendingCode(false);
                }
              }}
              disabled={resendingCode || !email.trim()}
              style={{
                width: '100%',
                padding: '0.625rem',
                marginTop: '0.5rem',
                borderRadius: '10px',
                border: '1.5px solid #E5E7EB',
                background: 'transparent',
                color: '#6B7280',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: (resendingCode || !email.trim()) ? 'not-allowed' : 'pointer',
                opacity: (resendingCode || !email.trim()) ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!resendingCode && email.trim()) {
                  e.target.style.borderColor = '#FFC720';
                  e.target.style.color = '#111827';
                }
              }}
              onMouseLeave={(e) => {
                if (!resendingCode && email.trim()) {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.color = '#6B7280';
                }
              }}
            >
              {resendingCode && (
                <span className="spinner spinner-small" style={{ marginRight: 0, borderColor: 'rgba(107, 114, 128, 0.3)', borderTopColor: 'rgba(107, 114, 128, 0.9)' }}></span>
              )}
              {resendingCode ? (t('login.resending') || 'Resending...') : (t('login.resendCode') || 'Resend Code')}
            </button>
          </div>
        ) : activeSignupTab === 'email' && !needsVerificationCode && !needsPhoneVerification ? (
          <div className="email-login-form" style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(4px)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
                flexDirection: 'column',
                gap: '1.5rem',
                border: '2px solid rgba(37, 99, 235, 0.3)',
                boxShadow: '0 0 20px rgba(37, 99, 235, 0.2)'
              }}>
                <span className="spinner" style={{ width: '32px', height: '32px', borderWidth: '4px', borderColor: 'rgba(37, 99, 235, 0.2)', borderTopColor: '#2563EB' }}></span>
                <span style={{ color: '#111827', fontSize: '1rem', fontWeight: '500' }}>
                  {isSignupMode
                    ? (t('login.signingUp') || 'Signing up...')
                    : (t('login.signingIn') || 'Signing in...')
                  }
                </span>
              </div>
            )}
            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="email" style={{ display: 'block', marginBottom: '0.625rem', color: '#111827', fontWeight: '600', fontSize: '0.9375rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                className="signup-input"
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  background: 'white',
                  color: '#111827',
                  fontSize: '1rem',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
                disabled={loading}
                onFocus={(e) => {
                  e.target.style.borderColor = '#FFC720';
                  e.target.style.boxShadow = '0 0 0 3px rgba(255, 199, 32, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="password" style={{ display: 'block', marginBottom: '0.625rem', color: '#111827', fontWeight: '600', fontSize: '0.9375rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                {t('login.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder') || 'Enter your password'}
                className="signup-input"
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  background: 'white',
                  color: '#111827',
                  fontSize: '1rem',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
                disabled={loading}
                onFocus={(e) => {
                  e.target.style.borderColor = '#FFC720';
                  e.target.style.boxShadow = '0 0 0 3px rgba(255, 199, 32, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.boxShadow = 'none';
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleEmailPasswordSignup();
                  }
                }}
              />
            </div>

            {isSignupMode && (
              <div style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="confirm-password" style={{ display: 'block', marginBottom: '0.625rem', color: '#111827', fontWeight: '600', fontSize: '0.9375rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                  {t('login.confirmPassword')}
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('login.confirmPasswordPlaceholder') || 'Confirm your password'}
                  className="signup-input"
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    background: 'white',
                    color: '#111827',
                    fontSize: '1rem',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  disabled={loading}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FFC720';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 199, 32, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'none';
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleEmailPasswordSignup();
                    }
                  }}
                />
              </div>
            )}

            {showAddPasswordForm && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                borderRadius: '8px',
                background: '#EFF6FF',
                border: '1px solid #BFDBFE'
              }}>
                <p style={{ color: '#111827', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  {t('login.addPasswordToGoogleAccount') || 'Add a password to your Google account so you can sign in with email and password:'}
                </p>
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="add-password" style={{ display: 'block', marginBottom: '0.5rem', color: '#111827', fontWeight: '500' }}>
                    {t('login.password') || 'Password'}
                  </label>
                  <input
                    id="add-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.passwordPlaceholder') || 'Enter password'}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '2px solid #E5E7EB',
                      background: 'white',
                      color: '#111827',
                      fontSize: '1rem'
                    }}
                    disabled={addingPassword}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="add-confirm-password" style={{ display: 'block', marginBottom: '0.5rem', color: '#111827', fontWeight: '500' }}>
                    {t('login.confirmPassword') || 'Confirm Password'}
                  </label>
                  <input
                    id="add-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('login.confirmPasswordPlaceholder') || 'Confirm password'}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '2px solid #E5E7EB',
                      background: 'white',
                      color: '#111827',
                      fontSize: '1rem'
                    }}
                    disabled={addingPassword}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !addingPassword && password.trim() && confirmPassword.trim()) {
                        handleAddPassword();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={handleAddPassword}
                  disabled={addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: (addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword)
                      ? '#E5E7EB'
                      : '#2563EB',
                    color: (addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword)
                      ? '#9CA3AF'
                      : 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: (addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword)
                      ? 'not-allowed'
                      : 'pointer',
                    marginBottom: '0.5rem'
                  }}
                >
                  {addingPassword ? (t('login.addingPassword') || 'Adding Password...') : (t('login.addPassword') || 'Add Password')}
                </button>
                <button
                  onClick={() => {
                    setShowAddPasswordForm(false);
                    setPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    background: 'transparent',
                    color: '#111827',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
              </div>
            )}

            {(() => {
              // Calculate button state
              const isAddPasswordDisabled = showAddPasswordForm && (
                addingPassword || !password.trim() || !confirmPassword.trim() || password !== confirmPassword
              );
              const isLoginDisabled = !showAddPasswordForm && (
                loading ||
                (needsVerificationCode && currentUser
                  ? verificationCode.trim().length !== 6
                  : !email.trim() ||
                    !password.trim() ||
                    (isSignupMode && (!confirmPassword.trim() || password !== confirmPassword)) ||
                    (needsVerificationCode && verificationCode.trim().length !== 6))
              );
              const isDisabled = isAddPasswordDisabled || isLoginDisabled;
              const buttonBackground = isDisabled ? '#E5E7EB' : '#FFC720';
              const buttonColor = isDisabled ? '#9CA3AF' : '#111827';
              const buttonCursor = isDisabled ? 'not-allowed' : 'pointer';

              return (
                <button
                  onClick={
                    showAddPasswordForm
                      ? handleAddPassword
                      : needsVerificationCode && currentUser
                        ? handleVerifyCode
                        : handleEmailPasswordSignup
                  }
                  disabled={isDisabled}
                  className="signup-primary-button"
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    borderRadius: '12px',
                    border: 'none',
                    background: buttonBackground,
                    color: buttonColor,
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: buttonCursor,
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    opacity: loading ? 0.5 : 1,
                    pointerEvents: loading ? 'none' : 'auto',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    boxShadow: isDisabled ? 'none' : '0 2px 8px rgba(255, 199, 32, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled && !loading) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 199, 32, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDisabled && !loading) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(255, 199, 32, 0.3)';
                    }
                  }}
                >
              {loading && (
                <div
                  className="signin-loading-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    flexDirection: 'column',
                    gap: '1rem',
                    border: '2px solid rgba(37, 99, 235, 0.5)',
                    boxShadow: '0 0 30px rgba(37, 99, 235, 0.4), inset 0 0 20px rgba(37, 99, 235, 0.1)',
                    animation: 'fadeInOverlay 0.3s ease-out'
                  }}
                >
                  <span
                    className="spinner"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderWidth: '4px',
                      borderColor: 'rgba(37, 99, 235, 0.3)',
                      borderTopColor: '#2563EB',
                      animation: 'spin 0.8s linear infinite',
                      display: 'block'
                    }}
                  ></span>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{
                      color: '#111827',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      letterSpacing: '0.3px',
                      display: 'block'
                    }}>
                      {needsVerificationCode && currentUser
                        ? (t('login.verifying') || 'Verifying...')
                        : (t('login.signingUp') || 'Signing up...')}
                    </span>
                    <span style={{
                      color: '#6B7280',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      display: 'block'
                    }}>
                      {t('login.pleaseWait') || 'Please wait while we sign you in'}
                    </span>
                  </div>
                </div>
              )}
                  <span style={{ opacity: (loading || addingPassword) ? 0 : 1, position: 'relative', zIndex: 1 }}>
                    {showAddPasswordForm
                      ? (t('login.addPassword') || 'Add Password')
                      : needsVerificationCode && currentUser
                        ? (t('login.verifyEmail') || 'Verify Email')
                        : (t('login.signUp') || 'Sign up')}
                  </span>
                </button>
              );
            })()}

            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280', textAlign: 'center' }}>
              {t('login.alreadyHaveAccount') || 'Already have an account?'}{' '}
              <span
                onClick={() => navigate('/login')}
                style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
              >
                {t('login.signIn') || 'Sign in'}
              </span>
            </div>
          </div>
        ) : activeSignupTab === 'google' && !needsPhoneVerification ? (
          <div style={{ position: 'relative', width: '100%', marginTop: 'var(--spacing-lg)' }}>
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="google-button"
              style={{
                opacity: loading ? 0.5 : 1,
                pointerEvents: loading ? 'none' : 'auto',
                position: 'relative',
                transition: 'all 0.3s ease',
                width: '100%'
              }}
            >
              {loading && (
                <div
                  className="signin-loading-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    flexDirection: 'column',
                    gap: '1rem',
                    border: '2px solid rgba(37, 99, 235, 0.5)',
                    boxShadow: '0 0 30px rgba(37, 99, 235, 0.4), inset 0 0 20px rgba(37, 99, 235, 0.1)',
                    animation: 'fadeInOverlay 0.3s ease-out'
                  }}
                >
                  <span
                    className="spinner"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderWidth: '4px',
                      borderColor: 'rgba(37, 99, 235, 0.3)',
                      borderTopColor: '#2563EB',
                      animation: 'spin 0.8s linear infinite',
                      display: 'block'
                    }}
                  ></span>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{
                      color: '#111827',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      letterSpacing: '0.3px',
                      display: 'block'
                    }}>
                      {t('login.signingUp') || 'Signing up...'}
                    </span>
                    <span style={{
                      color: '#6B7280',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      display: 'block'
                    }}>
                      {t('login.pleaseWait') || 'Please wait while we sign you up'}
                    </span>
                  </div>
                </div>
              )}
              {loading ? (
                <span
                  className="spinner spinner-small"
                  style={{
                    marginRight: 0,
                    borderColor: 'rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    width: '20px',
                    height: '20px',
                    borderWidth: '3px'
                  }}
                ></span>
              ) : (
                <svg className="google-icon" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span className="google-button-text">
                {loading ? t('login.signingIn') : t('login.signInWithGoogle')}
              </span>
            </button>

            <div className="login-footer">
              <p>{t('login.signInDescription')}</p>
            </div>
          </div>
        ) : null}

        {/* Phone Verification Screen - Focused View (shown after email verification or Google signup) */}

        {needsPhoneVerification && currentUser && (
          <div className="phone-verification-form" style={{ position: 'relative', marginTop: '1.5rem' }}>
            <div style={{
              marginBottom: '1.5rem',
              padding: '1.25rem',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
              border: '1px solid #BFDBFE',
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)'
            }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#1E40AF', fontSize: '1.25rem', fontWeight: '700', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                {t('login.phoneVerificationRequired') || 'Phone Verification Required'}
              </h3>
              <p style={{ margin: 0, color: '#3B82F6', fontSize: '0.9375rem', lineHeight: '1.5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                {t('login.phoneVerificationDescription') || 'Please verify your phone number to complete your account setup.'}
              </p>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="phone-number" style={{ display: 'block', marginBottom: '0.625rem', color: '#111827', fontWeight: '600', fontSize: '0.9375rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                {t('login.phone') || 'Phone Number'}
              </label>
              <input
                id="phone-number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={t('login.phonePlaceholder') || 'Enter your phone number'}
                className="signup-input"
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  background: 'white',
                  color: '#111827',
                  fontSize: '1rem',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
                disabled={loading || sendingPhoneCode}
                onFocus={(e) => {
                  e.target.style.borderColor = '#FFC720';
                  e.target.style.boxShadow = '0 0 0 3px rgba(255, 199, 32, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={{ color: '#6B7280', fontSize: '0.8125rem', marginTop: '0.5rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', lineHeight: '1.4' }}>
                {t('login.phoneFormatHint') || 'Enter an Israeli phone number (e.g., 050-1234567)'}
              </p>
            </div>

            {phoneCodeSent && (
              <div style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="phone-verification-code" style={{ display: 'block', marginBottom: '0.625rem', color: '#111827', fontWeight: '600', fontSize: '0.9375rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                  {t('login.verificationCode') || 'Verification Code'}
                </label>
                <input
                  id="phone-verification-code"
                  type="text"
                  value={phoneVerificationCode}
                  onChange={(e) => setPhoneVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  placeholder="123456"
                  className="signup-input verification-code-input"
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    background: 'white',
                    color: '#111827',
                    fontSize: '1.25rem',
                    letterSpacing: '0.4em',
                    textAlign: 'center',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  disabled={loading}
                  autoComplete='off'
                  onFocus={(e) => {
                    e.target.style.borderColor = '#FFC720';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 199, 32, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'none';
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && phoneVerificationCode.trim().length === 6) {
                      handleVerifyPhoneCode();
                    }
                  }}
                />
                <p style={{ color: '#6B7280', fontSize: '0.8125rem', marginTop: '0.5rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', lineHeight: '1.4', textAlign: 'center' }}>
                  {t('login.phoneCodeInstructions') || 'Enter the 6-digit code sent to your phone.'}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
              {!phoneCodeSent && (
                <button
                  type="button"
                  onClick={handleSendPhoneCode}
                  disabled={sendingPhoneCode || !phoneNumber.trim() || !isValidIsraeliPhone(phoneNumber)}
                  className="signup-primary-button"
                  style={{
                    flex: 1,
                    padding: '0.875rem 1.5rem',
                    borderRadius: '12px',
                    border: 'none',
                    background: (sendingPhoneCode || !phoneNumber.trim() || !isValidIsraeliPhone(phoneNumber))
                      ? '#E5E7EB'
                      : '#FFC720',
                    color: (sendingPhoneCode || !phoneNumber.trim() || !isValidIsraeliPhone(phoneNumber))
                      ? '#9CA3AF'
                      : '#111827',
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: (sendingPhoneCode || !phoneNumber.trim() || !isValidIsraeliPhone(phoneNumber))
                      ? 'not-allowed'
                      : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    transition: 'all 0.2s ease',
                    boxShadow: (sendingPhoneCode || !phoneNumber.trim() || !isValidIsraeliPhone(phoneNumber))
                      ? 'none'
                      : '0 2px 8px rgba(255, 199, 32, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!sendingPhoneCode && phoneNumber.trim() && isValidIsraeliPhone(phoneNumber)) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 199, 32, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!sendingPhoneCode && phoneNumber.trim() && isValidIsraeliPhone(phoneNumber)) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(255, 199, 32, 0.3)';
                    }
                  }}
                >
                  {sendingPhoneCode && (
                    <span className="spinner spinner-small" style={{ marginRight: 0, borderColor: 'rgba(17, 24, 39, 0.3)', borderTopColor: 'rgba(17, 24, 39, 0.9)' }}></span>
                  )}
                  {sendingPhoneCode ? (t('login.sending') || 'Sending...') : (t('login.sendPhoneCode') || 'Send Verification Code')}
                </button>
              )}
              {phoneCodeSent && (
                <button
                  type="button"
                  onClick={handleVerifyPhoneCode}
                  disabled={loading || phoneVerificationCode.trim().length !== 6}
                  className="signup-primary-button"
                  style={{
                    flex: 1,
                    padding: '0.875rem 1.5rem',
                    borderRadius: '12px',
                    border: 'none',
                    background: (loading || phoneVerificationCode.trim().length !== 6)
                      ? '#E5E7EB'
                      : '#FFC720',
                    color: (loading || phoneVerificationCode.trim().length !== 6)
                      ? '#9CA3AF'
                      : '#111827',
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: (loading || phoneVerificationCode.trim().length !== 6)
                      ? 'not-allowed'
                      : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    transition: 'all 0.2s ease',
                    boxShadow: (loading || phoneVerificationCode.trim().length !== 6)
                      ? 'none'
                      : '0 2px 8px rgba(255, 199, 32, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && phoneVerificationCode.trim().length === 6) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 199, 32, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && phoneVerificationCode.trim().length === 6) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(255, 199, 32, 0.3)';
                    }
                  }}
                >
                  {loading && (
                    <span className="spinner spinner-small" style={{ marginRight: 0, borderColor: 'rgba(17, 24, 39, 0.3)', borderTopColor: 'rgba(17, 24, 39, 0.9)' }}></span>
                  )}
                  {loading ? (t('login.verifying') || 'Verifying...') : (t('login.verifyPhone') || 'Verify Phone')}
                </button>
              )}
            </div>
          </div>
        )}

        <button
          onClick={() => navigate('/auth-choice')}
          className="back-to-homepage-btn"
        >
          {t('login.backToHomepage') || 'Back'}
        </button>
      </div>
    </div>
  );
}
